import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, Package, X } from "lucide-react";

const VARIANTS = ["white", "blue", "gold_crumple", "red", "orange", "purple"];
const STATUSES = ["wanted", "found", "fulfilled", "cancelled"];

export default function AdminWantedItems() {
  const [tab, setTab] = useState("wanted");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [demand, setDemand] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [l, d] = await Promise.all([
        api.get("/admin/wanted-items", { params: { status: tab, q: q || undefined, limit: 200 } }),
        api.get("/admin/wanted-items/demand"),
      ]);
      setRows(l.data.wanted_items || []);
      setDemand(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [tab]);

  async function transition(id, status) {
    try {
      await api.patch(`/admin/wanted-items/${id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)]" data-testid="admin-wanted-items">
      <div className="max-w-4xl mx-auto p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase text-[var(--smoke)]">CollectorUp Admin</div>
            <div className="text-2xl font-black flex items-center gap-2">
              <Package size={22}/> Wanted Items
            </div>
          </div>
          <button onClick={() => setShowAdd(true)}
                  data-testid="wanted-add-btn"
                  className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1">
            <Plus size={14}/> Add
          </button>
        </div>

        {/* Demand summary */}
        {demand && (demand.sticker_demand?.length > 0 || demand.shopify_demand?.length > 0) && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2"
                    data-testid="demand-summary">
            <div className="text-[10px] uppercase text-[var(--smoke)]">Active Demand</div>
            {demand.sticker_demand?.slice(0, 10).map((s, i) => (
              <div key={`s-${i}`} className="flex justify-between text-sm">
                <span>{s.sticker_code} · {s.variant?.toUpperCase()}</span>
                <span className="font-mono text-xs">
                  {s.customer_count} cust · qty {s.total_quantity}
                </span>
              </div>
            ))}
            {demand.shopify_demand?.slice(0, 10).map((s, i) => (
              <div key={`sh-${i}`} className="flex justify-between text-sm">
                <span>{s.product_title || s.shopify_variant_id}</span>
                <span className="font-mono text-xs">
                  {s.customer_count} cust · qty {s.total_quantity}
                </span>
              </div>
            ))}
          </section>
        )}

        {/* Filters */}
        <div className="flex gap-1 overflow-x-auto" data-testid="wanted-status-tabs">
          {["all", ...STATUSES].map((s) => (
            <button key={s}
                    data-testid={`wanted-tab-${s}`}
                    onClick={() => setTab(s === "all" ? "" : s)}
                    className={`text-xs px-3 py-1 rounded-full border-2 ${
                      (s === "all" ? "" : s) === tab
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "border-[var(--ink)]"
                    }`}>
              {s}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search sticker · product · customer…"
                 data-testid="wanted-search-input"
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="flex-1 border rounded-xl px-3 py-2 text-sm"/>
          <button onClick={load} data-testid="wanted-search-btn"
                  className="bg-[var(--ink)] text-white rounded-xl px-3 text-sm font-semibold">
            <Search size={14}/>
          </button>
        </div>

        {/* Rows */}
        <div className="space-y-2" data-testid="wanted-rows">
          {loading && <div className="text-sm text-[var(--smoke)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[var(--smoke)]">No wanted items.</div>
          )}
          {rows.map((w) => (
            <div key={w.id} data-testid={`wanted-row-${w.id}`}
                  className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white text-sm space-y-1">
              <div className="flex justify-between">
                <b>
                  {w.item_type === "sticker" && `${w.sticker_code} · ${w.variant?.toUpperCase()}`}
                  {w.item_type === "shopify_product" && (w.product_title || w.shopify_variant_id)}
                  {w.item_type === "custom" && w.description}
                </b>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  w.status === "wanted" ? "bg-yellow-200"
                    : w.status === "found" ? "bg-blue-200"
                    : w.status === "fulfilled" ? "bg-green-200"
                    : "bg-gray-200"
                }`}>{w.status}</span>
              </div>
              <div className="text-[11px] text-[var(--smoke)]">
                {w.public_wanted_id} · {w.public_customer_id} · qty {w.quantity}
                {w.target_price != null && ` · $${w.target_price}`}
                {w.source && ` · ${w.source}`}
              </div>
              {w.notes && <div className="text-xs">{w.notes}</div>}
              {(w.status === "wanted" || w.status === "found") && (
                <div className="flex gap-1 pt-1">
                  {w.status === "wanted" && (
                    <button onClick={() => transition(w.id, "found")}
                            data-testid={`wanted-mark-found-${w.id}`}
                            className="text-[11px] border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
                      Mark found
                    </button>
                  )}
                  {w.status === "found" && (
                    <button onClick={() => transition(w.id, "fulfilled")}
                            data-testid={`wanted-mark-fulfilled-${w.id}`}
                            className="text-[11px] border-2 border-green-600 text-green-800 rounded-full px-2 py-0.5">
                      Mark fulfilled
                    </button>
                  )}
                  <button onClick={() => transition(w.id, "cancelled")}
                          data-testid={`wanted-cancel-${w.id}`}
                          className="text-[11px] border-2 border-red-500 text-red-700 rounded-full px-2 py-0.5">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showAdd && <AddWanted onClose={() => setShowAdd(false)}
                             onCreated={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}


function AddWanted({ onClose, onCreated, defaultCustomerId }) {
  const [customerQ, setCustomerQ] = useState("");
  const [customer, setCustomer] = useState(null);
  const [type, setType] = useState("sticker");
  const [f, setF] = useState({
    sticker_code: "", variant: "gold_crumple",
    shopify_product_id: "", shopify_variant_id: "", product_title: "",
    description: "",
    quantity: 1, target_price: "", notes: "", source: "",
  });
  const [busy, setBusy] = useState(false);

  async function searchCustomer() {
    if (!customerQ.trim()) return;
    try {
      const r = await api.get("/admin/customers/list",
                                  { params: { q: customerQ, limit: 5 } });
      const rows = r.data.customers || [];
      if (rows.length === 0) toast.error("No customer found.");
      else setCustomer(rows[0]);
    } catch (e) { toast.error("Search failed."); }
  }

  useEffect(() => {
    if (defaultCustomerId) {
      api.get(`/admin/customers/${defaultCustomerId}`).then((r) => setCustomer(r.data));
    }
  }, [defaultCustomerId]);

  async function save() {
    if (!customer) return toast.error("Pick a customer first.");
    const body = { customer_id: customer.id, item_type: type,
                    quantity: Number(f.quantity) || 1,
                    target_price: f.target_price ? Number(f.target_price) : null,
                    notes: f.notes || null, source: f.source || null };
    if (type === "sticker") {
      body.sticker_code = f.sticker_code; body.variant = f.variant;
    } else if (type === "shopify_product") {
      body.shopify_product_id = f.shopify_product_id || null;
      body.shopify_variant_id = f.shopify_variant_id;
      body.product_title = f.product_title || null;
    } else {
      body.description = f.description;
    }
    setBusy(true);
    try {
      await api.post("/admin/wanted-items", body);
      toast.success("Wanted item added.");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end sm:items-center justify-center">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-4 space-y-2 max-h-[92vh] overflow-auto"
           data-testid="wanted-add-modal">
        <div className="flex items-center justify-between">
          <div className="font-bold">Add Wanted Item</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        {!defaultCustomerId && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase text-[var(--smoke)]">Customer</div>
            {customer ? (
              <div className="border-2 border-[var(--ink)] rounded-xl p-2 text-sm flex justify-between">
                <span>{customer.display_name || customer.email || customer.public_customer_id}</span>
                <button onClick={() => setCustomer(null)}
                        className="text-xs">change</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={customerQ} onChange={(e) => setCustomerQ(e.target.value)}
                       placeholder="Email / phone / public ID"
                       data-testid="wanted-customer-search"
                       onKeyDown={(e) => e.key === "Enter" && searchCustomer()}
                       className="flex-1 border rounded-xl px-3 py-2 text-sm"/>
                <button onClick={searchCustomer}
                        className="bg-[var(--ink)] text-white rounded-xl px-3 text-sm">
                  <Search size={14}/>
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-1" data-testid="wanted-type-tabs">
          {["sticker", "shopify_product", "custom"].map((t) => (
            <button key={t} onClick={() => setType(t)}
                    data-testid={`wanted-type-${t}`}
                    className={`text-xs px-2 py-1 rounded-full border-2 flex-1 ${
                      t === type ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--ink)]"
                    }`}>
              {t}
            </button>
          ))}
        </div>

        {type === "sticker" && (
          <>
            <input value={f.sticker_code}
                   onChange={(e) => setF({ ...f, sticker_code: e.target.value })}
                   placeholder="Sticker code · e.g. ARG-17"
                   data-testid="wanted-sticker-code"
                   className="w-full border rounded-xl px-3 py-2 text-sm"/>
            <select value={f.variant} onChange={(e) => setF({ ...f, variant: e.target.value })}
                    data-testid="wanted-variant"
                    className="w-full border rounded-xl px-3 py-2 text-sm">
              {VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </>
        )}
        {type === "shopify_product" && (
          <>
            <input value={f.shopify_product_id}
                   onChange={(e) => setF({ ...f, shopify_product_id: e.target.value })}
                   placeholder="Shopify product ID (optional)"
                   className="w-full border rounded-xl px-3 py-2 text-sm"/>
            <input value={f.shopify_variant_id}
                   onChange={(e) => setF({ ...f, shopify_variant_id: e.target.value })}
                   placeholder="Shopify variant ID *"
                   data-testid="wanted-shopify-variant"
                   className="w-full border rounded-xl px-3 py-2 text-sm"/>
            <input value={f.product_title}
                   onChange={(e) => setF({ ...f, product_title: e.target.value })}
                   placeholder="Product title (snapshot)"
                   className="w-full border rounded-xl px-3 py-2 text-sm"/>
          </>
        )}
        {type === "custom" && (
          <textarea value={f.description}
                    onChange={(e) => setF({ ...f, description: e.target.value })}
                    placeholder="e.g. Panini World Cup 2014 sealed box"
                    data-testid="wanted-description"
                    className="w-full border rounded-xl px-3 py-2 text-sm h-20"/>
        )}

        <div className="flex gap-2">
          <input type="number" min="1" value={f.quantity}
                 onChange={(e) => setF({ ...f, quantity: e.target.value })}
                 placeholder="Qty"
                 data-testid="wanted-qty"
                 className="w-24 border rounded-xl px-3 py-2 text-sm"/>
          <input type="number" step="0.01" value={f.target_price}
                 onChange={(e) => setF({ ...f, target_price: e.target.value })}
                 placeholder="Target $ (optional)"
                 className="flex-1 border rounded-xl px-3 py-2 text-sm"/>
        </div>
        <input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
               placeholder="Source · e.g. instagram / store / whatsapp"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <textarea value={f.notes}
                  onChange={(e) => setF({ ...f, notes: e.target.value })}
                  placeholder="Notes (optional)"
                  className="w-full border rounded-xl px-3 py-2 text-sm h-16"/>

        <button onClick={save} disabled={busy}
                data-testid="wanted-save-btn"
                className={`w-full rounded-2xl py-2 font-bold text-white ${
                  busy ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                }`}>
          {busy ? "Saving…" : "Add Wanted Item"}
        </button>
      </div>
    </div>
  );
}

export { AddWanted };
